import { useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import {
	useObjectCommentDispatchContext,
	useObjectCommentContext,
	AddFooterCommentMode,
} from '@confluence/comment-context/entry-points/ObjectCommentContext.ts';
import { useCommentsPanelState } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';

const OMNIBAR_ANALYTICS_SOURCE = 'confluenceOmniBar';

/**
 * Hook that encapsulates the comment triggering logic for the omnibar.
 * Handles expanding the comment container, showing the sticky draft,
 * and scrolling to the comment wrapper if already visible.
 */
export const useOmnibarCommentTrigger = (
	onCommentClick?: () => void,
): {
	handleCommentClick: () => void;
} => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { expandCommentContainer, setFooterEditorEntry } = useObjectCommentDispatchContext();
	const { expanded, isObjectCommentWrapperVisible, footerEditorEntry } = useObjectCommentContext();
	const { addFooterCommentAdf } = useCommentsPanelState();

	const handleCommentClick = useCallback((): void => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'omniBarCommentSection',
				source: OMNIBAR_ANALYTICS_SOURCE,
			},
		}).fire();

		const isShowStickyDraft =
			!isObjectCommentWrapperVisible &&
			(addFooterCommentAdf || expanded) &&
			footerEditorEntry !== AddFooterCommentMode.STICKY_DRAFT;

		if (!expanded) {
			expandCommentContainer({
				entryPoint: 'pageFooter',
			});
		}

		if (isShowStickyDraft || !isObjectCommentWrapperVisible) {
			setFooterEditorEntry(AddFooterCommentMode.STICKY_DRAFT);
		} else if (typeof document !== 'undefined') {
			document
				.querySelector(`[data-testid='object-comment-wrapper']`)
				?.scrollIntoView({ behavior: 'smooth' });
		}

		onCommentClick?.();
	}, [
		createAnalyticsEvent,
		expandCommentContainer,
		setFooterEditorEntry,
		expanded,
		isObjectCommentWrapperVisible,
		footerEditorEntry,
		addFooterCommentAdf,
		onCommentClick,
	]);

	return { handleCommentClick };
};
